<template>
  <section
    class="growth-dashboard"
    aria-labelledby="growth-title"
  >
    <div class="d-flex flex-wrap align-center justify-space-between mb-5">
      <div>
        <h2
          id="growth-title"
          class="text-h5 mb-1"
        >
          {{ $t("\n          Growth & acquisition\n        ") }}
        </h2><p class="mb-0 text--secondary">
          {{ $t("\n          See how people discover Self Biography and become members.\n        ") }}
        </p>
      </div>
      <v-btn
        color="primary"
        class="mt-3"
        @click="campaignDialog = true"
      >
        {{ $t("\n        Create campaign or event\n      ") }}
      </v-btn>
    </div>
    <v-alert
      v-if="error"
      type="error"
      outlined
    >
      {{ error }}
    </v-alert>
    <v-alert
      v-if="notice"
      type="success"
      outlined
      dismissible
      @input="notice = ''"
    >
      {{ notice }}
    </v-alert>
    <v-card
      outlined
      class="pa-4 mb-5"
    >
      <div class="growth-filters">
        <v-select
          v-model="preset"
          :items="presets"
          :label="$t(&quot;Period&quot;)"
          hide-details
          dense
          outlined
          @change="setPeriod"
        />
        <v-text-field
          v-model="from"
          type="date"
          :label="$t(&quot;From (UTC)&quot;)"
          hide-details
          dense
          outlined
        />
        <v-text-field
          v-model="to"
          type="date"
          :label="$t(&quot;Through (UTC)&quot;)"
          hide-details
          dense
          outlined
        />
        <v-select
          v-model="selectedCampaign"
          :items="campaignOptions"
          :label="$t(&quot;Campaign / event&quot;)"
          clearable
          hide-details
          dense
          outlined
        />
        <v-btn
          color="primary"
          :loading="loading"
          @click="applyFilters"
        >
          {{ $t("\n          Refresh report\n        ") }}
        </v-btn>
      </div>
    </v-card>
    <v-progress-linear
      v-if="loading"
      indeterminate
      :aria-label="$t(&quot;Loading growth report&quot;)"
    />
    <template v-if="report">
      <p class="text-caption text--secondary mb-4">
        {{ reportPeriod }}{{ $t(". Compared with the preceding ") }}{{ report.range.days }}{{ $t(" days. All dates use UTC.\n      ") }}
      </p>
      <v-row>
        <v-col
          v-for="card in cards"
          :key="card.label"
          cols="6"
          sm="6"
          lg="2"
        >
          <v-card
            outlined
            class="pa-4 fill-height growth-metric"
          >
            <div class="text-caption text--secondary">
              {{ card.label }}
            </div>
            <div class="text-h4 my-2">
              {{ number(card.value) }}
            </div>
            <div
              class="text-caption"
              :class="card.positive ? 'success--text' : 'text--secondary'"
            >
              {{ card.note }}
            </div>
          </v-card>
        </v-col>
      </v-row>
      <v-card
        outlined
        class="pa-4 mt-5"
      >
        <h3 class="text-h6">
          {{ $t("\n          Growth over time\n        ") }}
        </h3>
        <p class="text-caption text--secondary">
          {{ $t("\n          Sessions are browser visits, ending after 30 minutes of inactivity. Active accounts are distinct signed-in members who navigated the site.\n        ") }}
        </p>
        <apexchart
          type="line"
          height="300"
          :options="chartOptions"
          :series="chartSeries"
        />
      </v-card>
      <v-row class="mt-2">
        <v-col
          cols="12"
          md="6"
        >
          <v-card
            outlined
            class="pa-4 fill-height"
          >
            <h3 class="text-h6 mb-3">
              {{ $t("\n              Traffic sources\n            ") }}
            </h3>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>{{ $t("Source / medium") }}</th><th class="text-right">
                    {{ $t("\n                    Sessions\n                  ") }}
                  </th>
                </tr>
              </thead><tbody>
                <tr
                  v-for="row in report.sources"
                  :key="`${row.source}-${row.medium}`"
                >
                  <td>{{ row.source }} <span class="text--secondary">/ {{ row.medium }}</span></td><td class="text-right">
                    {{ number(row.sessions) }}
                  </td>
                </tr>
                <tr v-if="!report.sources.length">
                  <td
                    colspan="2"
                    class="text--secondary py-4"
                  >
                    {{ $t("\n                    No recorded sessions in this period.\n                  ") }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <v-card
            outlined
            class="pa-4 fill-height"
          >
            <h3 class="text-h6 mb-3">
              {{ $t("\n              Pages visited\n            ") }}
            </h3>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>{{ $t("Page") }}</th><th class="text-right">
                    {{ $t("\n                    Views\n                  ") }}
                  </th>
                </tr>
              </thead><tbody>
                <tr
                  v-for="row in report.pages"
                  :key="row.page"
                >
                  <td>{{ pageName(row.page) }}</td><td class="text-right">
                    {{ number(row.views) }}
                  </td>
                </tr>
                <tr v-if="!report.pages.length">
                  <td
                    colspan="2"
                    class="text--secondary py-4"
                  >
                    {{ $t("\n                    No recorded page views in this period.\n                  ") }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>
      <v-card
        outlined
        class="pa-4 mt-5"
      >
        <div class="d-flex flex-wrap justify-space-between align-center mb-2">
          <h3 class="text-h6">
            {{ $t("\n            Campaigns & physical events\n          ") }}
          </h3><v-btn
            small
            text
            color="primary"
            :disabled="!report.campaigns.length"
            @click="exportCampaigns"
          >
            {{ $t("\n            Export results\n          ") }}
          </v-btn>
        </div>
        <p class="text-caption text--secondary">
          {{ $t("\n          Signups are new accounts linked after their first sign-in to sessions started in this period. Attribution uses the first identified campaign within 30 days before registration. The rate measures those sessions that resulted in a signup by the end of this period.\n        ") }}
        </p>
        <v-data-table
          :headers="campaignHeaders"
          :items="report.campaigns"
          item-key="slug"
          :items-per-page="10"
          :no-data-text="$t(&quot;Create a campaign or event to get a tracking link and QR code.&quot;)"
        >
          <template v-slot:item.kind="{ item }">
            <v-chip
              small
              outlined
            >
              {{ item.kind === 'event' ? $t("Physical event") : $t("Marketing campaign") }}
            </v-chip>
          </template>
          <template v-slot:item.rate="{ item }">
            {{ rate(item) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn
              small
              text
              color="primary"
              @click="showLink(item)"
            >
              {{ $t("\n              Link & QR\n            ") }}
            </v-btn>
            <v-btn
              small
              text
              color="error"
              :disabled="removing"
              @click="requestRemoval(item)"
            >
              {{ $t("\n              Remove\n            ") }}
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
      <v-card
        outlined
        class="pa-4 mt-5"
      >
        <div class="d-flex flex-wrap align-center justify-space-between">
          <h3 class="text-h6">
            {{ $t("\n            New accounts\n          ") }}
          </h3><v-btn
            small
            text
            color="primary"
            :disabled="!report.members.length"
            @click="exportMembers"
          >
            {{ $t("\n            Export visible users\n          ") }}
          </v-btn>
        </div>
        <v-text-field
          v-model="search"
          class="growth-search mt-3"
          :label="$t(&quot;Search name or email&quot;)"
          outlined
          dense
          clearable
          @keydown.enter="applyFilters"
        >
          <template v-slot:append>
            <v-btn
              small
              text
              @click="applyFilters"
            >
              {{ $t("\n              Search\n            ") }}
            </v-btn>
          </template>
        </v-text-field>
        <v-data-table
          :headers="memberHeaders"
          :items="report.members"
          item-key="sub"
          hide-default-footer
          disable-pagination
          disable-sort
          :no-data-text="$t(&quot;No accounts match these dates and filters.&quot;)"
        >
          <template v-slot:item.name="{ item }">
            <div class="py-2 font-weight-medium">
              {{ item.name || $t("Name not provided") }}
            </div><div class="text-caption mb-2">
              {{ item.email || $t("Deleted account") }}
            </div>
          </template>
          <template v-slot:item.verified="{ item }">
            <v-chip
              small
              :color="Number(item.verified) ? 'success' : 'warning'"
              outlined
            >
              {{ Number(item.verified) ? $t("Verified") : $t("Pending") }}
            </v-chip><div
              v-if="!Number(item.enabled)"
              class="text-caption mt-1"
            >
              {{ $t("\n              Disabled\n            ") }}
            </div>
          </template>
          <template v-slot:item.registered_at="{ item }">
            {{ date(item.registered_at) }}
          </template>
          <template v-slot:item.last_seen_at="{ item }">
            {{ item.last_seen_at ? date(item.last_seen_at) : $t("Not recorded yet") }}
          </template>
          <template v-slot:item.campaign_name="{ item }">
            {{ item.campaign_name || $t("Unattributed") }}
          </template>
          <template v-slot:item.records="{ item }">
            {{ number(item.records) }}
          </template>
        </v-data-table>
        <div class="d-flex flex-wrap justify-space-between align-center mt-4">
          <span class="text-caption">{{ number(report.memberTotal) }}{{ $t(" matching accounts") }}</span><v-pagination
            v-if="report.memberTotal > 25"
            v-model="memberPage"
            :length="Math.ceil(report.memberTotal / 25)"
            :total-visible="5"
            @input="load"
          />
        </div>
      </v-card>
      <v-alert
        text
        color="primary"
        class="mt-5 text-caption"
      >
        {{ $t("\n        Visit and activity tracking started ") }}{{ date(report.meta.tracking_since) }}{{ $t(". Earlier account registrations are included from the account directory; earlier visits cannot be reconstructed.\r\n        Account directory last synchronized: ") }}{{ report.meta.accounts_synced_at ? date(report.meta.accounts_synced_at) : $t("Initial synchronization pending") }}{{ $t(".\r\n        The directory refreshes every 15 minutes; signed-in accounts update sooner. Unattributed accounts have no recorded campaign origin. Browser privacy settings can prevent visit tracking.\r\n      ") }}
      </v-alert>
    </template>
    <v-dialog
      v-model="removeDialog"
      max-width="460"
      :persistent="removing"
    >
      <v-card>
        <v-card-title>{{ $t("Remove campaign / event?") }}</v-card-title>
        <v-card-text>
          <p>{{ $t("Remove ") }}{{ removingCampaign ? removingCampaign.name : '' }}{{ $t(" from the list? Historical data and previously shared links will be kept.") }}</p>
          <v-alert
            v-if="removeError"
            type="error"
            outlined
          >
            {{ removeError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="removing"
            @click="removeDialog = false"
          >
            {{ $t("\n            Cancel\n          ") }}
          </v-btn>
          <v-btn
            color="error"
            :loading="removing"
            @click="removeCampaign"
          >
            {{ $t("\n            Remove\n          ") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="campaignDialog"
      max-width="600"
      scrollable
    >
      <v-card>
        <v-card-title>{{ $t("Create campaign or event") }}</v-card-title><v-card-text>
          <v-alert
            v-if="formError"
            type="error"
            outlined
          >
            {{ formError }}
          </v-alert>
          <v-text-field
            v-model="form.name"
            :label="$t(&quot;Name&quot;)"
            maxlength="120"
            outlined
            dense
          />
          <v-select
            v-model="form.kind"
            :items="[{ text: 'Physical event', value: 'event' }, { text: 'Marketing campaign', value: 'campaign' }]"
            :label="$t(&quot;Type&quot;)"
            outlined
            dense
            @change="changeKind"
          />
          <v-text-field
            v-model="form.source"
            :label="$t(&quot;Source&quot;)"
            :hint="$t(&quot;For example: conference, instagram, facebook or newsletter. Use letters, numbers, hyphens or underscores.&quot;)"
            persistent-hint
            outlined
            dense
            class="mb-5"
          />
          <v-text-field
            v-model="form.medium"
            :label="$t(&quot;Medium&quot;)"
            :hint="$t(&quot;For example: qr, email, social or print.&quot;)"
            persistent-hint
            outlined
            dense
            class="mb-5"
          />
          <v-select
            v-model="form.destination"
            :items="[{ text: 'Landing page', value: '/landingpage' }, { text: 'Biographies', value: '/biographies' }]"
            :label="$t(&quot;Destination&quot;)"
            outlined
            dense
          />
          <p class="text-caption">
            {{ $t("\n            A distinct link and downloadable QR code will be generated. Use a separate campaign for each event or marketing initiative you want to compare.\n          ") }}
          </p>
        </v-card-text><v-card-actions>
          <v-spacer /><v-btn
            text
            :disabled="saving"
            @click="campaignDialog = false"
          >
            {{ $t("\n            Cancel\n          ") }}
          </v-btn><v-btn
            color="primary"
            :loading="saving"
            @click="createCampaign"
          >
            {{ $t("\n            Create link\n          ") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="linkDialog"
      max-width="560"
    >
      <v-card>
        <v-card-title>{{ linkedCampaign ? linkedCampaign.name : $t("Tracking link") }}</v-card-title><v-card-text>
          <v-alert
            v-if="linkError"
            type="error"
            outlined
          >
            {{ linkError }}
          </v-alert>
          <div class="text-center">
            <img
              v-if="qrImage"
              :src="qrImage"
              :alt="$t(&quot;Campaign tracking QR code&quot;)"
              width="240"
              height="240"
              class="growth-qr"
            />
          </div>
          <v-text-field
            :value="trackingLink"
            :label="$t(&quot;Tracking link&quot;)"
            readonly
            outlined
            dense
            hide-details
          />
          <p class="text-caption mt-3">
            {{ $t("\n            Use this link in posts and emails, or print the QR code for the event. The short link keeps its campaign attribution automatically.\n          ") }}
          </p>
          <div class="d-flex flex-wrap growth-link-actions">
            <v-btn
              outlined
              color="primary"
              @click="copyLink"
            >
              {{ copied ? $t("Copied") : $t("Copy link") }}
            </v-btn><v-btn
              :disabled="!qrImage"
              :href="qrImage"
              :download="`${linkedCampaign ? linkedCampaign.slug : 'campaign'}-qr.png`"
              outlined
            >
              {{ $t("\n              Download QR\n            ") }}
            </v-btn>
          </div>
        </v-card-text><v-card-actions>
          <v-spacer /><v-btn
            text
            @click="linkDialog = false"
          >
            {{ $t("\n            Close\n          ") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>

import VueApexCharts from 'vue-apexcharts'
import QRCode from 'qrcode'
import { t } from '@/i18n'
import { growth } from '@/services/growth'

const today = () => new Date().toISOString().slice(0, 10)
const offset = (day, amount) => {
  const d = new Date(`${day}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + amount)

  return d.toISOString().slice(0, 10)
}
export default {
  name: 'GrowthDashboard',
  components: { apexchart: VueApexCharts },
  data: () => ({
    report: null,
    loading: false,
    error: '',
    notice: '',
    removeDialog: false,
    removingCampaign: null,
    removing: false,
    removeError: '',
    preset: 30,
    from: offset(today(), -29),
    to: today(),
    selectedCampaign: null,
    search: '',
    memberPage: 1,
    presets: [{ get text() { return t('Last 7 days') }, value: 7 }, { get text() { return t('Last 30 days') }, value: 30 }, { get text() { return t('Last 90 days') }, value: 90 }],
    campaignDialog: false,
    formError: '',
    saving: false,
    form: {
      name: '', kind: 'event', source: 'event', medium: 'qr', destination: '/landingpage',
    },
    linkedCampaign: null,
    linkDialog: false,
    qrImage: '',
    linkError: '',
    copied: false,
    requestNumber: 0,
    campaignHeaders: [{ get text() { return t('Campaign / event') }, value: 'name' }, { get text() { return t('Type') }, value: 'kind' }, { get text() { return t('Source') }, value: 'source' }, { get text() { return t('Medium') }, value: 'medium' }, { get text() { return t('Sessions') }, value: 'sessions' }, { get text() { return t('Signups') }, value: 'accounts' }, { get text() { return t('Signup rate') }, value: 'rate', sortable: false }, { text: '', value: 'actions', sortable: false }],
    memberHeaders: [{ get text() { return t('Member') }, value: 'name' }, { get text() { return t('Email status') }, value: 'verified' }, { get text() { return t('Registered (UTC)') }, value: 'registered_at' }, { get text() { return t('Last seen (UTC)') }, value: 'last_seen_at' }, { get text() { return t('Campaign / event') }, value: 'campaign_name' }, { get text() { return t('Memory records') }, value: 'records' }],
  }),
  computed: {
    campaignOptions() { return this.report ? this.report.campaigns.map(row => ({ text: row.name, value: row.slug })) : [] },
    reportPeriod() { return this.report ? `${this.report.range.start} through ${offset(this.report.range.end, -1)}` : '' },
    cards() {
      if (!this.report) return []
      const m = this.report.metrics
      const difference = (a, b) => `${a - b >= 0 ? '+' : ''}${this.number(a - b)} vs previous period`

      return [
        {
          get label() { return t('New accounts') }, value: m.newAccounts, note: difference(m.newAccounts, m.previousAccounts), positive: m.newAccounts > m.previousAccounts,
        },
        { get label() { return t('Verified new accounts') }, value: m.verifiedNewAccounts, note: 'Email verified' },
        {
          get label() { return t('Recorded sessions') }, value: m.sessions, note: difference(m.sessions, m.previousSessions), positive: m.sessions > m.previousSessions,
        },
        { get label() { return t('Page views') }, value: m.pageViews, note: 'Recorded navigation' },
        { get label() { return t('Active accounts') }, value: m.activeAccounts, note: 'Signed-in members' },
        { get label() { return t('Total accounts') }, value: m.totalAccounts, note: 'Created through the end date' },
      ]
    },
    days() {
      if (!this.report) return []

      return Array.from({ length: this.report.range.days }, (_, i) => offset(this.report.range.start, i))
    },
    chartSeries() {
      if (!this.report) return []
      const series = (key, name, measured) => {
        const values = new Map(this.report.daily[key].map(row => [String(row.day).slice(0, 10), Number(row[key])]))
        const tracking = (this.report.meta.tracking_since || '').slice(0, 10)

        return { name, data: this.days.map(day => (measured && day < tracking ? null : values.get(day) || 0)) }
      }

      return [series('accounts', 'New accounts', false), series('sessions', 'Sessions', true), series('active', 'Active accounts', true)]
    },
    chartOptions() {
      return {
        chart: { toolbar: { show: false }, zoom: { enabled: false }, foreColor: this.$vuetify.theme.dark ? '#c8c2da' : '#746e82' }, colors: ['#9155fd', '#28a89a', '#f2a53d'], stroke: { curve: 'straight', width: 2 }, markers: { size: 3 }, dataLabels: { enabled: false }, xaxis: { categories: this.days, tickAmount: Math.min(6, this.days.length), labels: { rotate: 0, formatter: value => String(value).slice(5) } }, yaxis: { min: 0, forceNiceScale: true, decimalsInFloat: 0 }, grid: { borderColor: this.$vuetify.theme.dark ? '#4c465f' : '#eeeaf4' }, legend: { position: 'top', horizontalAlign: 'left' }, tooltip: { theme: this.$vuetify.theme.dark ? 'dark' : 'light' }, noData: { get text() { return t('No data in this period') } },
      }
    },
    trackingLink() {
      if (!this.linkedCampaign) return ''
      const c = this.linkedCampaign
      if (/^[a-f0-9]{12}$/.test(c.short_code || '')) return new URL(`/c/${c.short_code}`, window.location.origin).toString()
      const url = new URL(c.destination, window.location.origin)
      url.search = new URLSearchParams({ utm_campaign: c.slug, utm_source: c.source, utm_medium: c.medium }).toString()

      return url.toString()
    },
  },
  mounted() { this.load() },
  methods: {
    number(value) { return Number(value || 0).toLocaleString(this.$locale()) },
    date(value) {
      if (!value) return t('Not available')
      const raw = String(value)
      const d = new Date(raw.includes('Z') || /[+-]\d\d:\d\d$/.test(raw) ? raw : `${raw.replace(' ', 'T')}Z`)

      return Number.isFinite(d.getTime()) ? d.toLocaleString(this.$locale(), { timeZone: 'UTC', dateStyle: 'medium', timeStyle: 'short' }) : raw
    },
    pageName(value) {
      return ({
        '/landingpage': 'Landing page', '/biographies': 'Biographies', '/auth': 'Sign in / registration', '/public/pictures': 'Public pictures', '/public/biography': 'Public biography', '/public/profile': 'Public profile', '/pages': 'Information pages', '/my-pictures': 'My Pictures', '/my-biography': 'My Biography', '/my-world': 'My World', '/my-diary': 'My Diary', '/billing': 'Plans & subscriptions', '/referrals': 'Referrals', '/other': 'Other pages',
      })[value] || value
    },
    rate(row) { return Number(row.sessions) ? `${((100 * Number(row.converted_sessions)) / Number(row.sessions)).toFixed(1)}%` : '0.0%' },
    setPeriod() { this.to = today(); this.from = offset(this.to, -(this.preset - 1)); this.applyFilters() },
    applyFilters() { this.memberPage = 1; this.load() },
    async load() {
      this.requestNumber += 1
      const number = this.requestNumber
      this.loading = true; this.error = ''
      try {
        const report = await growth.overview({
          start: this.from, end: offset(this.to, 1), campaign: this.selectedCampaign || '', search: this.search || '', page: this.memberPage,
        })
        if (number === this.requestNumber) this.report = report
      } catch (error) { if (number === this.requestNumber) this.error = (error.response && error.response.data.message) || t('Could not load growth data.') } finally { if (number === this.requestNumber) this.loading = false }
    },
    changeKind() { this.form.source = this.form.kind === 'event' ? 'event' : 'website'; this.form.medium = this.form.kind === 'event' ? 'qr' : 'social' },
    async createCampaign() {
      if (this.saving) return
      this.saving = true; this.formError = ''
      try {
        const campaign = await growth.createCampaign({ ...this.form, source: this.form.source.toLowerCase().trim(), medium: this.form.medium.toLowerCase().trim() })
        this.campaignDialog = false; this.form.name = ''; await this.load(); await this.showLink(campaign)
      } catch (error) { this.formError = (error.response && error.response.data.message) || t('Could not create the campaign.') } finally { this.saving = false }
    },
    requestRemoval(campaign) { this.removingCampaign = campaign; this.removeError = ''; this.removeDialog = true },
    async removeCampaign() {
      if (this.removing || !this.removingCampaign) return
      this.removing = true; this.removeError = ''
      try {
        await growth.removeCampaign(this.removingCampaign.slug)
        if (this.selectedCampaign === this.removingCampaign.slug) this.selectedCampaign = null
        this.memberPage = 1; this.removeDialog = false
        this.notice = t('Campaign / event removed.')
        await this.load()
      } catch (error) { this.removeError = (error.response && error.response.data.message) || t('Could not remove the campaign. Please try again.') } finally { this.removing = false }
    },
    async showLink(campaign) {
      this.linkedCampaign = campaign; this.linkDialog = true; this.qrImage = ''; this.linkError = ''; this.copied = false
      try { this.qrImage = await QRCode.toDataURL(this.trackingLink, { width: 720, margin: 3, errorCorrectionLevel: 'M' }) } catch (_) { this.linkError = t('Could not generate the QR code. The tracking link is still available.') }
    },
    async copyLink() {
      try { await navigator.clipboard.writeText(this.trackingLink); this.copied = true } catch (_) { this.linkError = 'Select and copy the tracking link above.' }
    },
    csv(name, headers, rows) {
      const cell = value => {
        const text = String(value === null || value === undefined ? '' : value)

        return `"${(/^[=+@\-\t\r]/.test(text) ? `'${text}` : text).replace(/"/g, '""')}"`
      }
      const csv = `\ufeff${[headers, ...rows].map(row => row.map(cell).join(',')).join('\r\n')}`
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
      const a = document.createElement('a'); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url)
    },
    exportCampaigns() { this.csv('self-biography-campaign-results.csv', ['Campaign', 'Type', 'Source', 'Medium', 'Sessions', 'Signups', 'Signup rate'], this.report.campaigns.map(c => [c.name, c.kind, c.source, c.medium, c.sessions, c.accounts, this.rate(c)])) },
    exportMembers() { this.csv('self-biography-new-accounts.csv', ['Name', 'Email', 'Status', 'Verified', 'Registered UTC', 'Last seen UTC', 'Campaign', 'Memory records'], this.report.members.map(m => [m.name, m.email, m.status, Number(m.verified) ? t('Yes') : t('No'), m.registered_at, m.last_seen_at, m.campaign_name, m.records])) },
  },
}
</script>

<style scoped>
.growth-dashboard { min-width: 0; }
.growth-filters { display: grid; grid-template-columns: 150px 170px 170px minmax(180px, 1fr) auto; gap: 12px; align-items: center; }
.growth-metric { border-top: 3px solid #9155fd; }
.growth-search { max-width: 420px; }
.growth-qr { max-width: 100%; object-fit: contain; background: white; }
.growth-link-actions { gap: 10px; }
@media (max-width: 1100px) { .growth-filters { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 500px) { .growth-filters { grid-template-columns: minmax(0, 1fr); } }
</style>
